import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const statusChip = (status) => {
    if (status === "draft") return <span className="text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full text-xs font-medium">Draft</span>;
    return <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Published</span>;
};

// Search box + column visibility + density, all built into MUI's DataGrid
const CustomToolbar = () => (
    <GridToolbarContainer className="p-3 flex flex-wrap gap-2 items-center justify-between border-b">
        <div className="flex gap-1">
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
        </div>
        <GridToolbarQuickFilter placeholder="Search products..." />
    </GridToolbarContainer>
);

const ProductTable = () => {
    const { enqueueSnackbar } = useSnackbar();

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const productPerms = user.adminRole?.permissions?.products || {};
    const canCreate = isSuperAdmin || productPerms.create;
    const canEdit = isSuperAdmin || productPerms.edit;
    const canDelete = isSuperAdmin || productPerms.delete;
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [pageSize, setPageSize] = useState(25);
    // Remembers which page you were on across navigating away (e.g. to edit
    // a product) and back — otherwise the grid always resets to page 1.
    const [page, setPage] = useState(() => Number(sessionStorage.getItem('adminProductsPage')) || 0);

    // Filters persist in sessionStorage so navigating away (e.g. to edit a
    // product) and back doesn't reset what you were looking at.
    const [categoryFilter, setCategoryFilterState] = useState(() => sessionStorage.getItem('adminProductsCategoryFilter') || "");
    const [brandFilter, setBrandFilterState] = useState(() => sessionStorage.getItem('adminProductsBrandFilter') || "");
    const [statusFilter, setStatusFilterState] = useState(() => sessionStorage.getItem('adminProductsStatusFilter') || "");
    const [noImageFilter, setNoImageFilterState] = useState(() => sessionStorage.getItem('adminProductsNoImageFilter') === 'true');

    const setCategoryFilter = (val) => { setCategoryFilterState(val); sessionStorage.setItem('adminProductsCategoryFilter', val); };
    const setBrandFilter = (val) => { setBrandFilterState(val); sessionStorage.setItem('adminProductsBrandFilter', val); };
    const setStatusFilter = (val) => { setStatusFilterState(val); sessionStorage.setItem('adminProductsStatusFilter', val); };
    const setNoImageFilter = (val) => { setNoImageFilterState(val); sessionStorage.setItem('adminProductsNoImageFilter', val); };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/api/v1/admin/products');
            setProducts(data.products);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load products", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                const flatten = (cats, depth = 0) => cats.flatMap((c) => [
                    { id: c._id, name: `${'— '.repeat(depth)}${c.name}` },
                    ...(c.children ? flatten(c.children, depth + 1) : []),
                ]);
                setCategories(flatten(data.categories));
            } catch (error) {
                console.error('Failed to load categories', error);
            }
        };
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const brandOptions = useMemo(() => {
        const names = products.map((p) => p.brand?.name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [products]);

    const trashHandler = async (id) => {
        try {
            await axios.put(`/api/v1/admin/product/${id}/trash`);
            enqueueSnackbar("Moved to trash", { variant: "success" });
            setProducts((prev) => prev.filter((p) => p._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to trash product", { variant: "error" });
        }
    };

    const bulkTrashHandler = async () => {
        if (!window.confirm(`Move ${selectionModel.length} product(s) to trash?`)) return;
        try {
            await Promise.all(selectionModel.map((id) => axios.put(`/api/v1/admin/product/${id}/trash`)));
            enqueueSnackbar(`${selectionModel.length} product(s) moved to trash`, { variant: "success" });
            setProducts((prev) => prev.filter((p) => !selectionModel.includes(p._id)));
            setSelectionModel([]);
        } catch (error) {
            enqueueSnackbar("Some products failed to trash", { variant: "error" });
        }
    };

    const filteredProducts = useMemo(() => {
        return products.filter((p) => {
            if (categoryFilter && !(p.categories || []).some((c) => (c._id || c) === categoryFilter)) return false;
            if (brandFilter && p.brand?.name !== brandFilter) return false;
            if (statusFilter && (p.status || "published") !== statusFilter) return false;
            if (noImageFilter && p.images && p.images.length > 0) return false;
            return true;
        });
    }, [products, categoryFilter, brandFilter, statusFilter, noImageFilter]);

    const activeFilters = [
        categoryFilter && { key: "category", label: `Category: ${categories.find((c) => c.id === categoryFilter)?.name || ""}`, clear: () => setCategoryFilter("") },
        brandFilter && { key: "brand", label: `Brand: ${brandFilter}`, clear: () => setBrandFilter("") },
        statusFilter && { key: "status", label: `Status: ${statusFilter}`, clear: () => setStatusFilter("") },
        noImageFilter && { key: "noImage", label: "No Image", clear: () => setNoImageFilter(false) },
    ].filter(Boolean);

    const columns = [
        {
            field: "name",
            headerName: "Name",
            minWidth: 220,
            flex: 1,
            renderCell: (params) => (
                <div className="flex items-center gap-3">
                    <img draggable="false" src={params.row.image} alt={params.row.name} className="w-10 h-10 rounded object-cover border" />
                    <span>{params.row.name}</span>
                </div>
            ),
        },
        {
            field: "category",
            headerName: "Category",
            minWidth: 160,
            flex: 0.6,
        },
        {
            field: "brand",
            headerName: "Brand",
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 100,
            flex: 0.3,
            renderCell: (params) => statusChip(params.row.status),
        },
        {
            field: "outOfStock",
            headerName: "Availability",
            minWidth: 120,
            flex: 0.3,
            renderCell: (params) => (
                params.row.outOfStock
                    ? <span className="font-medium text-red-700 rounded-full bg-red-100 px-2 py-0.5">Out of Stock</span>
                    : <span className="font-medium text-green-700 rounded-full bg-green-100 px-2 py-0.5">In Stock</span>
            ),
        },
        {
            field: "price",
            headerName: "Sale Price",
            type: "number",
            minWidth: 110,
            flex: 0.3,
            renderCell: (params) => <span>HK${params.row.price?.toLocaleString()}</span>,
        },
        {
            field: "cprice",
            headerName: "Price",
            type: "number",
            minWidth: 100,
            flex: 0.3,
            renderCell: (params) => <span className="text-gray-500 line-through">HK${params.row.cprice?.toLocaleString()}</span>,
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 110,
            flex: 0.3,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    {canEdit && (
                        <Link to={`/admin/product/${params.row.id}`} className="text-primary-blue hover:bg-blue-50 p-1.5 rounded-full"><EditIcon sx={{ fontSize: "20px" }} /></Link>
                    )}
                    {canDelete && (
                        <button onClick={() => trashHandler(params.row.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full"><DeleteOutlineIcon sx={{ fontSize: "20px" }} /></button>
                    )}
                </div>
            ),
        },
    ];

    const rows = filteredProducts.map((item) => ({
        id: item._id,
        name: item.name,
        image: item.images && item.images.length > 0 ? item.images[0].url : "https://placehold.co/100x100?text=No+Image",
        category: item.categories && item.categories.length > 0 ? item.categories.map((c) => c.name).join(", ") : "—",
        brand: item.brand?.name || "—",
        status: item.status || "published",
        outOfStock: item.outOfStock,
        price: item.price,
        cprice: item.cuttedPrice,
    }));

    return (
        <>
            <MetaData title="Admin Products | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Products</h1>
                <div className="flex gap-3">
                    {canDelete && (
                        <Link to="/admin/products/trash" className="py-2 px-4 rounded shadow font-medium text-gray-700 bg-white border hover:shadow-lg flex items-center gap-1.5">
                            <RestoreFromTrashIcon sx={{ fontSize: "18px" }} /> Trash
                        </Link>
                    )}
                    {canCreate && (
                        <Link to="/admin/new_product" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Product</Link>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-3 p-4 border-b">
                    <TextField label="Category" select size="small" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ minWidth: 180 }}>
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((c) => <MenuItem value={c.id} key={c.id}>{c.name}</MenuItem>)}
                    </TextField>
                    <Autocomplete
                        options={brandOptions}
                        value={brandFilter || null}
                        onChange={(e, newValue) => setBrandFilter(newValue || "")}
                        size="small"
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => <TextField {...params} label="Brand" placeholder="Search brands..." />}
                    />
                    <TextField label="Status" select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                    </TextField>
                    <FormControlLabel
                        control={<Checkbox checked={noImageFilter} onChange={(e) => setNoImageFilter(e.target.checked)} />}
                        label="No Image Only"
                    />

                    {canDelete && selectionModel.length > 0 && (
                        <button onClick={bulkTrashHandler} className="ml-auto flex items-center gap-1.5 text-sm bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded hover:bg-red-100">
                            <DeleteSweepIcon sx={{ fontSize: "18px" }} /> Move {selectionModel.length} to Trash
                        </button>
                    )}
                </div>

                {/* Active filter chips */}
                {activeFilters.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b bg-gray-50">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {activeFilters.map((f) => (
                            <Chip key={f.key} label={f.label} size="small" onDelete={f.clear} />
                        ))}
                        <button
                            onClick={() => { setCategoryFilter(""); setBrandFilter(""); setStatusFilter(""); }}
                            className="text-xs text-primary-blue hover:underline ml-1"
                        >
                            Clear All
                        </button>
                    </div>
                )}

                <div style={{ height: 600 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        checkboxSelection={canDelete}
                        disableSelectIconOnClick
                        rowHeight={64}
                        page={page}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            sessionStorage.setItem('adminProductsPage', newPage);
                        }}
                        pageSize={pageSize}
                        onPageSizeChange={(newSize) => setPageSize(newSize)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        selectionModel={selectionModel}
                        onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                        components={{ Toolbar: CustomToolbar }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f9fafb', fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280' },
                            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default ProductTable;